import { useEffect, useRef, useState } from 'react';
import { Briefcase, Plus, Loader2 } from 'lucide-react';
import { useMattersStore } from '../../store/mattersStore';
import { Button } from '../ui';

interface CasePickerProps {
  value: string | null;
  onChange: (caseId: string | null) => void;
  /** Compact style for tight headers (JobDetail, framed card). */
  compact?: boolean;
  disabled?: boolean;
}

const MINI_INPUT_CLS =
  'w-full text-sm border border-legal-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-accent';

/**
 * "File under a case (optional)" — small case select with a proper inline
 * mini-form for "＋ New case" (title + optional client; replaces the old
 * window.prompt()).
 */
export function CasePicker({ value, onChange, compact = false, disabled = false }: CasePickerProps) {
  const { matters, fetchMatters, createMatter } = useMattersStore();
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newClient, setNewClient] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  useEffect(() => {
    if (showNewForm) titleRef.current?.focus();
  }, [showNewForm]);

  const handleSelect = (v: string) => {
    if (v === '__new__') {
      setShowNewForm(true);
      return;
    }
    onChange(v || null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newTitle.trim() || isCreating) return;
    setIsCreating(true);
    setError('');
    try {
      const matter = await createMatter(newTitle.trim(), newClient.trim() || undefined);
      onChange(matter.id);
      setShowNewForm(false);
      setNewTitle('');
      setNewClient('');
    } catch {
      setError('Could not create the case.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={`relative flex items-center ${compact ? 'space-x-1' : 'space-x-2'}`}>
      {isCreating ? (
        <Loader2 className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} animate-spin text-legal-400`} />
      ) : matters.length === 0 ? (
        <Plus className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-legal-400`} />
      ) : (
        <Briefcase className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-legal-400`} />
      )}
      <select
        value={value ?? ''}
        onChange={(e) => handleSelect(e.target.value)}
        disabled={disabled || isCreating}
        aria-label="File under a case (optional)"
        data-track="case-picker"
        className={`border border-legal-200 rounded-md text-legal-700 bg-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60 ${
          compact ? 'text-xs px-2 py-1 max-w-[180px]' : 'text-sm px-3 py-2'
        }`}
      >
        <option value="">No case</option>
        {matters.map((m) => (
          <option key={m.id} value={m.id}>
            {m.title}
            {m.client_name ? ` (${m.client_name})` : ''}
          </option>
        ))}
        <option value="__new__">+ New case…</option>
      </select>

      {/* Inline "new case" mini-form (popover) — title + optional client */}
      {showNewForm && (
        <form
          onSubmit={handleCreate}
          className="absolute z-20 top-full right-0 mt-1.5 w-72 max-w-[85vw] bg-white border border-legal-200 rounded-lg shadow-elevation-2 p-3 space-y-2 animate-slide-up"
        >
          <p className="text-xs font-semibold text-legal-700">New case</p>
          <input
            ref={titleRef}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            maxLength={300}
            placeholder='Case title — e.g. "State v. Ravi — bail"'
            className={MINI_INPUT_CLS}
          />
          <input
            value={newClient}
            onChange={(e) => setNewClient(e.target.value)}
            maxLength={255}
            placeholder="Client name (optional)"
            className={MINI_INPUT_CLS}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              data-track="case-picker-create"
              isLoading={isCreating}
              disabled={!newTitle.trim()}
            >
              Create case
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowNewForm(false);
                setError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
